// What the task pane reports to Sentry when a call to the backend fails
// (GID-83): failed API calls and chat streams that fail or are cut off.
//
// Only failures someone should look at: 5xx, network drops and timeouts.
// 4xx are the app working as designed (sign-in expired, terms changed, rate
// limit, not found) and would bury the real ones. An event names the call by
// method and route template, never by body, query string or message text, and
// the same failure is sent at most once a minute per page so an outage does
// not flood the project. Kept apart from sentry.js so it is unit-tested
// without the Sentry SDK.

/* global navigator */

import { getSentryChatContext, isSentryEnabled, sentryCaptureMessage } from "./sentry.js";

export const FAILURE_THROTTLE_MS = 60 * 1000;

const ID_SEGMENT_PATTERNS = [
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i, // uuid
  /^[0-9a-f]{8,}$/i, // Mongo ObjectId, hashes
  /^\d+$/, // numbers
  /^(?=.*\d)[A-Za-z0-9_.-]{16,}$/, // long opaque ids (msg_..., file ids)
];

// Reporting these would recurse (Sentry's own ingest) or report the call
// that turns Sentry on.
const IGNORED_URL_PATTERNS = [/\/client-config(?:[/?#]|$)/i, /\/envelope\/?(?:[?#]|$)/i, /sentry\.io/i, /\.ingest\./i];

// Stream errors that are expected flows, not failures (see api.js).
const EXPECTED_STREAM_ERROR_TYPES = new Set([
  "abort_error",
  "auth_error",
  "authorization_error",
  "rate_limit",
  "terms_required",
  "plan_not_fresh",
]);

const REQUEST_ID_PATTERN = /^[A-Za-z0-9._:-]{1,128}$/;
const ERROR_CODE_PATTERN = /^[a-z][a-z0-9_]{0,39}$/;

let lastSentAt = new Map();
let reporting = false;

function isIdSegment(segment) {
  return ID_SEGMENT_PATTERNS.some((pattern) => pattern.test(segment));
}

function decodeSegment(segment) {
  try {
    return decodeURIComponent(segment);
  } catch (_) {
    return segment;
  }
}

/**
 * The route a call went to, with ids replaced by `{id}` and the query string
 * dropped, so one broken endpoint is one Sentry issue however many sessions hit
 * it -- and no id, search term or file name reaches Sentry.
 */
export function templateApiRoute(url) {
  const raw = String(url || "");
  let path;
  try {
    path = new URL(raw, "http://pane.invalid").pathname;
  } catch (_) {
    path = raw.split(/[?#]/)[0];
  }
  const segments = path
    .replace(/^\/api\/v\d+(?=\/|$)/, "")
    .split("/")
    .filter(Boolean)
    .map((segment) => (isIdSegment(decodeSegment(segment)) ? "{id}" : segment));
  return `/${segments.join("/")}`;
}

function isIgnoredUrl(url) {
  return IGNORED_URL_PATTERNS.some((pattern) => pattern.test(String(url || "")));
}

function isCancelled(error) {
  return Boolean(error?.__CANCEL__) || error?.code === "ERR_CANCELED" || error?.name === "CanceledError";
}

function isTimeoutCode(code) {
  return code === "ECONNABORTED" || code === "ETIMEDOUT";
}

/**
 * Whether an axios error is one to report: a 5xx, or a request that got no
 * answer (network drop, timeout). Not 4xx, not a call the user cancelled, not
 * a request that never left the pane.
 */
export function isReportableApiFailure(error) {
  if (!error || isCancelled(error)) return false;
  if (isIgnoredUrl(`${error.config?.baseURL || ""}${error.config?.url || ""}`)) return false;
  const status = Number(error.response?.status);
  if (status) return status >= 500;
  return Boolean(error.request) || isTimeoutCode(error.code);
}

function readRequestId(headers) {
  if (!headers) return null;
  const value = typeof headers.get === "function" ? headers.get("x-request-id") : headers["x-request-id"];
  return typeof value === "string" && REQUEST_ID_PATTERN.test(value) ? value : null;
}

function isOnline() {
  return typeof navigator === "undefined" ? null : navigator.onLine;
}

/** The Sentry message and scope for a failed API call; no body, no query. */
export function buildApiFailureReport(error) {
  const method = String(error?.config?.method || "get").toUpperCase();
  const route = templateApiRoute(error?.config?.url);
  const status = Number(error?.response?.status) || null;
  let errorType = "network_error";
  if (status) errorType = "server_error";
  else if (isTimeoutCode(error?.code)) errorType = "timeout";
  const label = status || (errorType === "timeout" ? "timeout" : "network error");
  return {
    message: `API ${label} ${method} ${route}`,
    options: {
      level: "error",
      fingerprint: ["api-failure", method, route],
      tags: {
        subsystem: "api_client",
        http_status: status || "none",
        http_method: method,
        api_route: route,
        error_type: errorType,
      },
      contexts: {
        api_failure: {
          status,
          error_type: errorType,
          request_id: readRequestId(error?.response?.headers),
          online: isOnline(),
        },
      },
    },
  };
}

/** True when this fingerprint was not sent in the last minute; records it. */
export function shouldSendFailure(fingerprint, now = Date.now()) {
  const key = fingerprint.join("|");
  const last = lastSentAt.get(key);
  if (last !== undefined && now - last < FAILURE_THROTTLE_MS) return false;
  lastSentAt = new Map(lastSentAt).set(key, now);
  return true;
}

export function resetFailureThrottle() {
  lastSentAt = new Map();
}

// Reporting must never break the caller's error handling, nor report itself.
function capture(report) {
  if (reporting || !isSentryEnabled()) return false;
  if (!shouldSendFailure(report.options.fingerprint)) return false;
  reporting = true;
  try {
    sentryCaptureMessage(report.message, report.options);
    return true;
  } finally {
    reporting = false;
  }
}

/** Called from the axios response interceptor with the raw axios error. */
export function reportApiFailure(error) {
  try {
    if (!isReportableApiFailure(error)) return false;
    return capture(buildApiFailureReport(error));
  } catch (reportError) {
    console.warn("Could not report the API failure to Sentry:", reportError?.message);
    return false;
  }
}

/**
 * The kind of a stream failure and its HTTP status, from the error api.js
 * hands onError. The fetch path throws plain Errors that handleError wraps as
 * unknown_error; their (fixed, content-free) messages say what happened.
 */
export function classifyStreamFailure(error) {
  const status = Number(error?.status) || null;
  if (error?.type && error.type !== "unknown_error") return { type: error.type, status };
  const text = String(error?.details?.message || error?.message || "");
  const httpStatus = text.match(/HTTP error! status: (\d{3})/);
  if (httpStatus) return { type: "http_error", status: Number(httpStatus[1]) };
  if (/timeout/i.test(text)) return { type: "timeout", status };
  if (/network|failed to fetch|ERR_INCOMPLETE_CHUNKED_ENCODING/i.test(text)) return { type: "network_error", status };
  if (/No data received/i.test(text)) return { type: "empty_stream", status };
  return { type: "unknown_error", status };
}

export function isReportableStreamFailure(error) {
  if (!error) return false;
  const { type, status } = classifyStreamFailure(error);
  if (EXPECTED_STREAM_ERROR_TYPES.has(type)) return false;
  return !(status >= 400 && status < 500);
}

/**
 * The Sentry message and scope for a chat turn that failed. The trace is the
 * turn's own when the server sent one, else the newest reply's in the same
 * chat (what "Report a problem" would name).
 */
export function buildStreamFailureReport(error, { sessionId = null, traceId = null } = {}) {
  const { type, status } = classifyStreamFailure(error);
  const code = typeof error?.code === "string" && ERROR_CODE_PATTERN.test(error.code) ? error.code : null;
  const known = getSentryChatContext();
  const session = sessionId || known.sessionId || null;
  const trace = traceId || (known.sessionId === session ? known.traceId : null);
  return {
    message: `Chat stream failed: ${type}`,
    options: {
      level: "error",
      fingerprint: ["chat-stream-failure", type, code || "none"],
      tags: {
        subsystem: "chat_stream",
        session_id: session,
        langfuse_trace_id: trace,
        stream_error_type: type,
        stream_error_code: code,
        http_status: status,
      },
      contexts: {
        chat_stream: { error_type: type, error_code: code, status, online: isOnline() },
      },
    },
  };
}

/**
 * Called by chatService once per turn that did not end in a reply. `error` is
 * what api.js handed onError, or a synthetic `{type: "server_error_chunk",
 * code}` / `{type: "stream_incomplete"}`.
 */
export function reportStreamFailure(error, turn = {}) {
  try {
    if (!isReportableStreamFailure(error)) return false;
    return capture(buildStreamFailureReport(error, turn));
  } catch (reportError) {
    console.warn("Could not report the chat stream failure to Sentry:", reportError?.message);
    return false;
  }
}
